import {
  cloneObject,
  getPlantLastModifyByIcon,
  getPlantLastModifyByLabel,
  parseShutterActuatorSettings,
  getUserInitialsForBubble,
} from '@/core/utils/utils';
import {
  SET_APP_LOADING,
  SET_SELECTED_PLANT,
  SET_SELECTED_PLANT_ID,
  SET_CONTEXTUAL_MENU_ITEMS,
} from '@/core/store/action-types';
import { mapGetters, mapState } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import MenuSharing from './MenuSharing/MenuSharing.vue';
import {
  RoomDTO,
  ServerModel,
  PlantDetailDTO,
  PlantSharingDTO,
  PlantType,
} from '@/../client-generator/generated-clients/plants';
import MegaMenuItem, { MenuItemRoute } from '@/core/models/menu-items';
import { FETCH_PLANT, HANDLE_REFRESH_PLANT } from '@/core/utils/emitter-events';
import ConfirmPlantUpdated from './ConfirmPlantUpdated/ConfirmPlantUpdated.vue';
import AddRoomObjectDialog from './AddRoomObjectDialog/AddRoomObjectDialog.vue';
import DialogPlantCreate from '../PlantsHub/DialogPlantCreate/DialogPlantCreate.vue';
import DialogPlantDuplicate from '../PlantsHub/DialogPlantDuplicate/DialogPlantDuplicate.vue';
import DialogStructureCreate from './PlantStructure/DialogStructureCreate/DialogStructureCreate.vue';
import DialogConfirmDeletePlant from '@/views/components/DialogConfirmDeletePlant/DialogConfirmDeletePlant.vue';
import DialogConfirmTransferOwnership from '@/views/components/DialogConfirmTransferOwnership/DialogConfirmTransferOwnership.vue';

const MHS1Icon = require('@/assets/images/icons/icon-server-mhs-1.svg');
const F460Icon = require('@/assets/images/icons/icon-server-f460.svg');
const C300EOSIcon = require('@/assets/images/icons/icon-mx-server.svg');

@Options({
  name: 'PlantDetail',
  watch: {
    menuItemsContextualWithActiveClasses(newValue) {
      this.$store.dispatch(SET_CONTEXTUAL_MENU_ITEMS, newValue);
    },
  },
  components: {
    MenuSharing,
    DialogPlantCreate,
    AddRoomObjectDialog,
    ConfirmPlantUpdated,
    DialogPlantDuplicate,
    DialogStructureCreate,
    DialogConfirmDeletePlant,
    DialogConfirmTransferOwnership,
  },
  computed: {
    ...mapState(['currentPlant']),
    ...mapGetters('UI', ['plantEditEnabled', 'plantDeleteEnabled']),
  },
})
export default class AppResourcePlantDetail extends Vue {
  PlantType = PlantType;
  plantEditEnabled!: boolean;
  plantDeleteEnabled!: boolean;
  currentPlant!: PlantDetailDTO;
  selectedPlantId: number | null = null;
  isPlantDuplicateDialogVisible = false;
  tempPlant: PlantDetailDTO | null = null;

  get menuItemsContextual(): MegaMenuItem[] {
    return [
      {
        label: this.$t('AppResource.Structure'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/icons/icon-general-view.svg')});`,
        to: {
          name: 'page-plant-dashboard',
        },
      },
      {
        label: this.currentPlant?.mxServer
          ? this.$t(
              `AppResource.${ServerModel[this.currentPlant?.mxServer?.model!]}`
            )
          : 'No plant type',
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${this.serverIcon});`,
        to: {
          name: 'page-plant-settings',
        },
      },
      {
        label: this.$t('AppResource.Devices'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/icons/icon-network.svg')});`,
        to: {
          name: 'page-plant-devices',
        },
      },
      {
        label: this.$t('AppResource.GeneralCommandsAndGroups'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/icons/icon-command.svg')});`,
        to: {
          name: 'page-commands-groups',
        },
      },
      {
        label: this.$t('AppResource.Scenes'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/icons/icon-automation.svg')});`,
        to: {
          name: 'page-plant-scenes',
        },
      },
      {
        label: this.$t('AppResource.Alarmsystem'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/icons/icon-security.svg')});`,
        to: {
          name: 'page-plant-alarmsystem',
        },
        visible: this.currentPlant?.mxServer?.isAlarmConfigured ?? false,
      },
      {
        label: this.$t('menuLabel.notesAndDocuments'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/icons/icon-paperclip.svg')});`,
        visible: false,
      },
    ];
  }

  isPlantDirty = false;
  isVisibleEditDialog = false;
  isDevicesCardLoading = false;
  isVisibleDeletePlantConfirm = false;
  isVisibleConfirmPlantUpdated = false;
  isVisibleAddRoomObjectDialog = false;
  isVisibleCreateStructureDialog = false;
  isVisibleTransferOwnershipConfirm = false;

  currentRoom: any = null;

  isPausedFetchupdate = false;
  intervalFetchUpdate: any | null = null;
  timeoutRenewSession: any | null = null;

  get iconDevice() {
    return getPlantLastModifyByIcon(this.currentPlant?.lastModifyByDesktop);
  }

  get iconDeviceLabel() {
    return getPlantLastModifyByLabel(this.currentPlant?.lastModifyByDesktop);
  }

  get menuItemsContextualWithActiveClasses(): MegaMenuItem[] {
    return this.menuItemsContextual.map((menuItem) => {
      let additionalClass = '';
      if (menuItem.to && (menuItem.to as MenuItemRoute).name) {
        additionalClass = this.getMenuItemClass(
          (menuItem.to as MenuItemRoute).name
        );
      }

      return {
        ...menuItem,
        class: `${menuItem.class} ${additionalClass}`,
      };
    });
  }

  get username() {
    return `${this.currentPlant?.lastModifyBy?.name} ${this.currentPlant?.lastModifyBy?.surname}`;
  }

  get userInitials() {
    return getUserInitialsForBubble(this.currentPlant.lastModifyBy);
  }

  get hideFooter() {
    return [
      'page-plant-room',
      'page-scene-wizard',
      'page-plant-scenes',
      'page-plant-devices',
      'page-command-wizard',
      'page-plant-settings',
      'page-commands-groups',
      'page-plant-alarmsystem',
      'page-assign-room-object',
      'page-assign-thermo-room-object',
    ].includes(this.$route.name as string);
  }

  get favouriteIcon() {
    return require(`@/assets/images/icons/icon-favourite${
      !this.currentPlant?.isFavourite ? '-outline' : ''
    }.svg`);
  }

  get plantName() {
    if (this.currentPlant) {
      return this.currentPlant.name;
    }

    return '';
  }

  get serverIcon() {
    switch (this.currentPlant?.mxServer?.model) {
      case ServerModel.Classe300EOS:
        return C300EOSIcon;
      case ServerModel.F460:
      case ServerModel.F461:
        return F460Icon;
      case ServerModel.MyHomeServer1:
        return MHS1Icon;
      default:
        return MHS1Icon;
    }
  }

  created() {
    this.$store.dispatch(SET_SELECTED_PLANT, null);
  }

  unmounted() {
    this.$store.dispatch(SET_SELECTED_PLANT, null);
  }

  async mounted() {
    this.$store.dispatch(
      SET_CONTEXTUAL_MENU_ITEMS,
      this.menuItemsContextualWithActiveClasses
    );

    this.selectedPlantId = parseInt(this.$route.params.plantId.toString(), 10);
    this.$store.dispatch(SET_SELECTED_PLANT_ID, this.selectedPlantId);
    await this.fetchPlant();

    this.registerEmitterEvents();
    this.registerPeriodicFetchUpdates();
    this.renewSessionTimeout();
  }

  beforeUnmount() {
    this.unregisterEmitterEvents();
    this.unregisterPeriodicFetchUpdates();
    this.unregisterRenewSessionTimeout();

    this.$store.dispatch(SET_SELECTED_PLANT_ID, null);
  }

  async fetchPlant() {
    if (!this.selectedPlantId) return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      const plant = await plantsClient.plantGET(
        this.selectedPlantId,
        undefined
      );
      plant.rooms?.forEach((room) => {
        room.roomObjects?.forEach((roomObject) => {
          roomObject.devices?.forEach((device) => {
            device.defaultSettings?.forEach((ds) => {
              parseShutterActuatorSettings(ds);
            });
          });
        });
      });

      this.$store.dispatch(SET_SELECTED_PLANT, plant);
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  registerEmitterEvents() {
    this.$emitter.on(FETCH_PLANT, this.fetchPlant);
    this.$emitter.on(
      HANDLE_REFRESH_PLANT,
      this.handleRefreshPlantFromOuterEvent
    );
  }

  unregisterEmitterEvents() {
    this.$emitter.off(FETCH_PLANT, this.fetchPlant);
    this.$emitter.off(
      HANDLE_REFRESH_PLANT,
      this.handleRefreshPlantFromOuterEvent
    );
  }

  registerPeriodicFetchUpdates() {
    this.intervalFetchUpdate = setTimeout(async () => {
      await this.doFetchUpdatesInternal();
      this.registerPeriodicFetchUpdates();
    }, this.$config.PLANT_REFRESH_INTERVAL);
  }

  unregisterPeriodicFetchUpdates() {
    clearTimeout(this.intervalFetchUpdate);
  }

  private async doFetchUpdatesInternal() {
    if (this.isPausedFetchupdate) {
      return;
    }

    try {
      const newPlant = await plantsClient.plantGET(
        this.selectedPlantId as number,
        this.currentPlant?.lastModify
      );

      if (newPlant && newPlant.desktopId) {
        this.isVisibleConfirmPlantUpdated = true;
        this.isPausedFetchupdate = true;
        this.tempPlant = newPlant;
      }
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    }
  }

  async renewSessionTimeout() {
    if (!this.selectedPlantId) return;
    try {
      var renewSecondsDto = await plantsClient.session(this.selectedPlantId);
      this.timeoutRenewSession = setTimeout(
        this.renewSessionTimeout,
        (renewSecondsDto.renewSessionSeconds ?? 60) * 1000
      );
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    }
  }

  unregisterRenewSessionTimeout() {
    clearTimeout(this.timeoutRenewSession);
  }

  handleRefreshPlantFromOuterEvent() {
    this.isPausedFetchupdate = true;
    this.isVisibleConfirmPlantUpdated = true;
  }

  getMenuItemClass(routeName: string) {
    if (this.$route.name === routeName) {
      return 'p-menuitem-active';
    }

    return '';
  }

  toggleAddRoomObjectDialog(room: RoomDTO) {
    this.currentRoom = this.isVisibleAddRoomObjectDialog ? null : room;
    this.isVisibleAddRoomObjectDialog = !this.isVisibleAddRoomObjectDialog;
  }

  toggleCreateStructureDialog() {
    this.isVisibleCreateStructureDialog = !this.isVisibleCreateStructureDialog;
  }

  toggleEditDialog() {
    this.isVisibleEditDialog = !this.isVisibleEditDialog;
  }

  getParsedDate(date?: Date) {
    if (!date) return '';
    const options: any = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleString('en-GB', options);
  }

  toggleDeletePlantConfirm() {
    this.isVisibleDeletePlantConfirm = !this.isVisibleDeletePlantConfirm;
  }

  toggleTransferOwnershipConfirm() {
    this.isVisibleTransferOwnershipConfirm = !this
      .isVisibleTransferOwnershipConfirm;
  }

  async handleDeletePlant() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await plantsClient.plantDELETE(this.currentPlant?.desktopId as number);
      this.$router.push({
        name: 'page-plants-hub',
      });
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async handleDuplicatePlant() {
    this.isPlantDuplicateDialogVisible = true;
  }

  handleDuplicatePlantFull() {
    alert(`Duplicate full`);
  }

  async hideConfirmPlantUpdated() {
    this.isVisibleConfirmPlantUpdated = false;

    if (!this.tempPlant) {
      // Handle refresh plant triggered by
      // an event (API call) from another
      // (sub)component
      await this.fetchPlant();
    } else {
      // Handle refresh plant triggered by
      // the periodic API call for
      // checking if the plant was updated.
      // The plant was already fetched from
      // the aforementioned API call and
      // stored in tempPlant.

      this.$store.dispatch(
        SET_SELECTED_PLANT,
        PlantDetailDTO.fromJS(cloneObject(this.tempPlant))
      );
      this.tempPlant = null;
    }

    this.isPausedFetchupdate = false;
  }

  handleInvitedUser(userEmail: string) {
    const newUser = new PlantSharingDTO({
      invitedEmail: userEmail,
      confirmed: false,
    });

    this.currentPlant?.sharedWith?.push(newUser);
  }

  handleRemoveSharingUser(user: PlantSharingDTO) {
    const userIndex = this.currentPlant?.sharedWith?.findIndex(
      (u) => u.userId === user.userId
    );

    if (userIndex != null && userIndex >= 0) {
      this.currentPlant?.sharedWith?.splice(userIndex, 1);
    }
  }

  async togglePlantFromFavorites() {
    if (!this.currentPlant?.desktopId) return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      this.currentPlant.isFavourite = await plantsClient.togglePlantFromFavorites(
        this.currentPlant.desktopId
      );
      this.currentPlant.lastModify = new Date();
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  onSaveAndExitClick(): void {
    this.$router.replace({
      path: '/',
      replace: true,
    });
  }

  async handleHidePlantDuplicateDialog() {
    this.isPlantDuplicateDialogVisible = false;
  }

  async handlePlantDuplicated() {
    this.onSaveAndExitClick();
  }
}
